import BrandingStudio from "@/components/BrandingStudio";
import { CompanyLoginSecurity } from "@/components/LoginSecuritySettings";
import DocumentTemplates from "@/components/DocumentTemplates";
import { useSearchParams } from "react-router-dom";
import { useMoodTracking } from "@/lib/useMoodTracking";
import FieldlinkSetup from '@/components/trades/FieldlinkSetup';
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Image as ImageIcon, Upload, Trash2, FileDown, AlertTriangle, Banknote, Save, Clock3, Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import QuoteBrandingSettings from "./QuoteBrandingSettings";

// White-label org logo. Once uploaded, this logo appears on every PDF
// (timesheets, payroll, pay-run detail, SSSP) instead of the Fieldlink brand
// strip, with a small "Generated by fieldlink.co.nz" attribution in the footer.
export default function Branding() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const sections = [
    ["company", "Company", "Company identity and logo."],
    ["branding", "Branding", "Your workspace identity, logos and colours."],
    ["documents", "Documents", "Appearance of quotes and customer documents."],
    ["pay", "Billing & pay", "Billing details, pay rules and company costs."],
    ["team", "Team", "Notifications and optional mood check-ins."],
    ...(["admin", "super_admin"].includes(user?.role) ? [["security", "Security", "Login protection for your company."]] : []),
    ["setup", "App setup", "How Fieldlink is configured for your company."],
  ];
  const section = sections.some(([key]) => key === params.get("section")) ? params.get("section") : "company";
  const showSection = key => ({display: section === key ? undefined : "none"});
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewBust, setPreviewBust] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/org/branding");
      setInfo(data || {});
    } catch (e) {
      toast.error("Failed to load branding");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking same file re-fires
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
    const lower = file.name.toLowerCase();
    const okType = allowed.includes(file.type) || lower.endsWith(".svg") || lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg");
    if (!okType) { toast.error("Logo must be PNG, JPG, or SVG"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo too large (max 2MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/org/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated — it'll show on all new PDF exports");
      setPreviewBust(Date.now());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove the company logo? PDFs will revert to Fieldlink branding.")) return;
    try {
      await api.delete("/org/branding/logo");
      toast.success("Logo removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  const tok = typeof window !== "undefined" ? localStorage.getItem("fl_token") || "" : "";
  const logoUrl = info?.logo_path
    ? `${api.defaults.baseURL}/files/view?path=${encodeURIComponent(info.logo_path)}&auth=${encodeURIComponent(tok)}&v=${previewBust}`
    : null;

  // Office and admin can manage branding (per user choice 4b at setup time)
  const canManage = ["admin", "office", "super_admin"].includes(user?.role);

  return (
    <div className={`p-4 sm:p-6 lg:p-8 ${["documents", "branding"].includes(section) ? "max-w-[1800px]" : "max-w-5xl"} mx-auto pb-24 lg:pb-10`} data-testid="branding-page">
      <PageHeader
        tag="/ Org settings"
        title="Company Settings"
        subtitle="Manage your company details and preferences."
      />

      <nav aria-label="Company settings sections" className="my-6 flex flex-wrap gap-2 border-b border-border pb-4">
        {sections.map(([key, label]) => <button key={key} type="button" aria-pressed={section === key}
          onClick={() => setParams(previous => { const next = new URLSearchParams(previous); next.set("section", key); return next; })}
          className={`min-h-11 rounded-lg px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5F15] ${section === key ? "bg-[#FF5F15] text-white" : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
          {label}
        </button>)}
      </nav>
      <p className="mb-5 text-sm text-muted-foreground">{sections.find(([key]) => key === section)?.[2]}</p>
      {section === "branding" && <BrandingStudio />}
      <div style={showSection("setup")}><FieldlinkSetup /></div>
      {loading ? (
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>
      ) : (
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3" style={showSection("security")}><CompanyLoginSecurity enabled={info?.enhanced_login_security} onChanged={enabled => setInfo(previous => ({ ...previous, enhanced_login_security: enabled }))} /></div>
          {/* Logo card */}
          <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("company")} data-testid="branding-logo-card">
            <div className="label-tag">Company logo</div>
            <h2 className="font-semibold tracking-tight text-lg mt-2 mb-4">
              {info?.logo_path ? "Current logo" : "No logo yet"}
            </h2>

            {/* Preview */}
            <div className="border-2 border-zinc-200 bg-[#F4F4F5] p-6 flex items-center justify-center min-h-[180px]" data-testid="branding-preview">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company logo preview"
                  className="max-h-32 max-w-full object-contain"
                  data-testid="branding-preview-img"
                />
              ) : (
                <div className="text-center text-zinc-400">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2" strokeWidth={1.5} />
                  <div className="text-xs font-mono uppercase tracking-widest">No logo uploaded</div>
                  <div className="text-[10px] mt-1">PDFs are currently using the Fieldlink badge</div>
                </div>
              )}
            </div>

            {/* Actions */}
            {canManage && (
              <div className="mt-5 flex flex-wrap gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,.svg,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={onUpload}
                  data-testid="branding-file-input"
                />
                <Button
                  onClick={onPick}
                  disabled={uploading}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                  data-testid="branding-upload-button">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading…" : info?.logo_path ? "Replace logo" : "Upload logo"}
                </Button>
                {info?.logo_path && (
                  <Button
                    onClick={onRemove}
                    variant="outline"
                    className="rounded-sm border-zinc-400 text-zinc-700 hover:bg-zinc-100"
                    data-testid="branding-remove-button">
                    <Trash2 className="w-4 h-4 mr-2" /> Remove
                  </Button>
                )}
              </div>
            )}
            {!canManage && (
              <div className="mt-5 text-xs text-zinc-500 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" />
                Only admins and office staff can change the company logo.
              </div>
            )}
          </div>

          <details className="rounded-lg border border-border p-4 lg:col-span-3 text-sm" style={showSection("company")} data-testid="branding-specs-card">
            <summary className="cursor-pointer font-medium">Logo guidance</summary>
            <p className="mt-3 text-muted-foreground">Use a PNG, JPG or SVG under 2 MB. A wide logo with a transparent background works best. Your logo appears on document exports, including site diaries, timesheets, payroll summaries and SSSPs.</p>
          </details>

          {canManage && <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("documents")}><DocumentTemplates /></div>}
          {/* Quote branding — customer-facing proposal */}
          <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("documents")} data-testid="quote-branding-card">
            <QuoteBrandingSettings canManage={canManage} info={info} reload={load} />
          </div>

          {/* Document settings */}
          <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("company")} data-testid="document-settings-card">
            <DocumentSettings canManage={canManage} />
          </div>

          {/* Billing details */}
          <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("pay")} data-testid="billing-settings-card">
            <BillingSettings canManage={canManage} />
          </div>

          {/* Overtime rules */}
          <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("pay")} data-testid="overtime-settings-card">
            <OvertimeSettings canManage={canManage} />
          </div>

          <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("team")}>
            <MoodTrackingSettings canManage={canManage} />
          </div>

          {/* Team notification switches */}
          <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("team")} data-testid="notification-settings-card">
            <NotificationSettings canManage={canManage} />
          </div>

          {/* Overheads — admin eyes only */}
          {["admin", "super_admin"].includes(user?.role) && (
            <div className="hard-card rounded-lg p-5 sm:p-6 lg:col-span-3" style={showSection("pay")} data-testid="overheads-settings-card">
              <OverheadsSettings />
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function MoodTrackingSettings({ canManage }) {
  const { enabled, loading, error, refresh } = useMoodTracking();
  const [saving, setSaving] = useState(false);
  const save = async next => {
    setSaving(true);
    try {
      const {data} = await api.patch("/org/engagement-settings", {mood_tracking_enabled: next});
      await refresh(data, {revalidate: false});
      toast.success(next ? "Mood tracking turned on" : "Mood tracking turned off");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update mood tracking"); }
    finally { setSaving(false); }
  };
  return <section data-testid="mood-tracking-settings">
    <div className="flex items-start justify-between gap-5">
      <div><h2 className="font-semibold text-lg" id="mood-tracking-label">Mood tracking</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl" id="mood-tracking-help">Optional mood check-ins after timesheets and a Team Pulse heat map. Turn this off to stop collecting moods and hide the heat map for your company.</p>
      </div>
      <Switch aria-labelledby="mood-tracking-label" aria-describedby="mood-tracking-help" checked={enabled} disabled={!canManage || loading || !!error || saving} onCheckedChange={save} data-testid="mood-tracking-toggle" />
    </div>
    {error ? <p className="mt-3 text-sm text-red-600">Could not load this setting. <button className="underline" onClick={() => refresh()}>Retry</button></p> : <p role="status" className="mt-3 text-sm font-medium">{loading ? "Loading…" : saving ? "Saving…" : enabled ? "On" : "Off"}</p>}
    <p className="mt-2 text-xs text-muted-foreground">Changes save automatically. Timesheets and payroll are unaffected. Previous check-ins are retained if you turn tracking on again.</p>
  </section>;
}


// Document settings — company identity printed on branded PDF documents
// (site diary, and progressively the other Fieldlink document exports).
function DocumentSettings({ canManage }) {
  const [form, setForm] = useState({
    legal_name: "", trading_name: "", nzbn: "", website: "", document_footer_note: "",
  });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/org/me")
      .then(({ data }) => setForm({
        legal_name: data?.legal_name || "",
        trading_name: data?.trading_name || "",
        nzbn: data?.nzbn || "",
        website: data?.website || "",
        document_footer_note: data?.document_footer_note || "",
      }))
      .finally(() => setLoaded(true));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/org/me", form);
      toast.success("Document settings saved — they'll show on your PDF exports");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="document-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-tag flex items-center gap-1.5"><FileDown className="w-3 h-3" /> Document settings</div>
          <h2 className="font-semibold tracking-tight text-lg mt-2">
            Your identity on every document
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Printed in the header of branded PDFs like the Daily Site Diary — alongside your logo, billing address, phone and email from the sections below.
          </p>
        </div>
        {canManage && (
          <Button onClick={save} disabled={busy || !loaded}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
            data-testid="document-settings-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save document settings"}
          </Button>
        )}
      </div>
      {!loaded ? (
        <div className="h-24 bg-zinc-50 animate-pulse" />
      ) : (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <Label className="label-tag" htmlFor="ds-trading">Trading name</Label>
            <Input id="ds-trading" value={form.trading_name} onChange={set("trading_name")} disabled={!canManage}
              placeholder="Shown big on documents — defaults to your company name"
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="doc-trading-name" />
          </div>
          <div>
            <Label className="label-tag" htmlFor="ds-legal">Legal name</Label>
            <Input id="ds-legal" value={form.legal_name} onChange={set("legal_name")} disabled={!canManage}
              placeholder="e.g. ACME Drainage and Civil Limited"
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="doc-legal-name" />
          </div>
          <div>
            <Label className="label-tag" htmlFor="ds-nzbn">NZBN</Label>
            <Input id="ds-nzbn" value={form.nzbn} onChange={set("nzbn")} disabled={!canManage}
              placeholder="9429000000000"
              className="mt-1.5 rounded-sm border-zinc-400 font-mono" data-testid="doc-nzbn" />
          </div>
          <div>
            <Label className="label-tag" htmlFor="ds-web">Website</Label>
            <Input id="ds-web" value={form.website} onChange={set("website")} disabled={!canManage}
              placeholder="www.yourbusiness.co.nz"
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="doc-website" />
          </div>
          <div className="md:col-span-2">
            <Label className="label-tag" htmlFor="ds-footer">Document footer message (optional)</Label>
            <Input id="ds-footer" value={form.document_footer_note} onChange={set("document_footer_note")} disabled={!canManage}
              placeholder="Thank you for your business."
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="doc-footer-note" />
          </div>
        </div>
      )}
    </div>
  );
}


// Billing details — bank account, GST #, payment terms and invoice footer.
// Lives on the same page as branding so admins set up everything in one go.
function BillingSettings({ canManage }) {
  const [form, setForm] = useState({
    gst_registered: true,
    gst_number: "",
    bank_account_name: "",
    bank_account_number: "",
    payment_terms_days: 14,
    invoice_cutoff_day: "",
    payment_terms_text: "",
    billing_address: "",
    billing_contact_email: "",
    billing_contact_phone: "",
    invoice_footer_note: "",
  });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/org/me")
      .then(({ data }) => {
        if (!mounted) return;
        setForm((f) => ({
          ...f,
          gst_registered: data?.gst_registered !== false,
          gst_number: data?.gst_number || "",
          bank_account_name: data?.bank_account_name || "",
          bank_account_number: data?.bank_account_number || "",
          payment_terms_days: data?.payment_terms_days ?? 14,
          invoice_cutoff_day: data?.invoice_cutoff_day ?? "",
          payment_terms_text: data?.payment_terms_text || "",
          billing_address: data?.billing_address || "",
          billing_contact_email: data?.billing_contact_email || "",
          billing_contact_phone: data?.billing_contact_phone || "",
          invoice_footer_note: data?.invoice_footer_note || "",
        }));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => { mounted = false; };
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        payment_terms_days: parseInt(form.payment_terms_days, 10) || 14,
        invoice_cutoff_day: form.invoice_cutoff_day ? parseInt(form.invoice_cutoff_day, 10) : null,
      };
      await api.patch("/org/me", payload);
      toast.success("Billing details saved — they'll appear on every new quote & invoice");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="billing-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-tag flex items-center gap-1.5"><Banknote className="w-3 h-3" /> Billing details</div>
          <h2 className="font-semibold tracking-tight text-lg mt-2">
            How customers see &amp; pay you
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Printed on every quote and invoice PDF. Bank account + GST # are critical — customers can&apos;t pay you without them.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={save}
            disabled={busy || !loaded}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
            data-testid="billing-save"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save billing details"}
          </Button>
        )}
      </div>

      {!canManage ? (
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Only admins and office staff can edit billing details.
        </div>
      ) : !loaded ? (
        <div className="h-32 bg-zinc-50 animate-pulse" />
      ) : (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
          <div className="md:col-span-2 flex items-start justify-between gap-3 rounded-sm border border-zinc-200 dark:border-white/10 p-3.5" data-testid="gst-registered-row">
            <div>
              <Label className="label-tag" id="gst-registered-label">GST registered</Label>
              <p className="text-[11px] text-zinc-500 mt-1 max-w-md">
                Turn this OFF if your company is not GST registered. No GST will be added or shown on any new quote or invoice, and the GST number is hidden.
              </p>
            </div>
            <Switch
              aria-labelledby="gst-registered-label"
              checked={form.gst_registered}
              onCheckedChange={(v) => setForm({ ...form, gst_registered: !!v })}
              className="data-[state=checked]:bg-[#FF5F15]"
              data-testid="gst-registered-toggle"
            />
          </div>
          {form.gst_registered && (
          <div>
            <Label className="label-tag" htmlFor="bs-gst">GST number</Label>
            <Input
              id="bs-gst"
              value={form.gst_number}
              onChange={set("gst_number")}
              placeholder="123-456-789"
              className="mt-1.5 rounded-sm border-zinc-400 font-mono"
              data-testid="billing-gst"
            />
            <p className="text-[10px] text-zinc-400 mt-1">9 digits, IRD-issued.</p>
          </div>
          )}
          <div>
            <Label className="label-tag" htmlFor="bs-terms">Default payment terms (days)</Label>
            <Input
              id="bs-terms"
              type="number"
              min="0"
              max="180"
              value={form.payment_terms_days}
              onChange={set("payment_terms_days")}
              className="mt-1.5 rounded-sm border-zinc-400 font-mono"
              data-testid="billing-terms"
            />
            <p className="text-[10px] text-zinc-400 mt-1">New invoices default to today + this many days. NZ trades commonly use 7, 14 or 20.</p>
          </div>
          <div>
            <Label className="label-tag" htmlFor="bs-cutoff">Invoice cut-off day of month</Label>
            <Input
              id="bs-cutoff"
              type="number"
              min="1"
              max="31"
              value={form.invoice_cutoff_day}
              onChange={set("invoice_cutoff_day")}
              placeholder="e.g. 20"
              className="mt-1.5 rounded-sm border-zinc-400 font-mono"
              data-testid="billing-cutoff-day"
            />
            <p className="text-[10px] text-zinc-400 mt-1">If you invoice on the 20th, hour imports and "Invoice now" only pull hours up to the most recent 20th. Leave blank to import everything.</p>
          </div>
          <div>
            <Label className="label-tag" htmlFor="bs-bank-name">Bank account name</Label>
            <Input
              id="bs-bank-name"
              value={form.bank_account_name}
              onChange={set("bank_account_name")}
              placeholder="ACME Civil Ltd"
              className="mt-1.5 rounded-sm border-zinc-400"
              data-testid="billing-bank-name"
            />
          </div>
          <div>
            <Label className="label-tag" htmlFor="bs-bank-no">Bank account number</Label>
            <Input
              id="bs-bank-no"
              value={form.bank_account_number}
              onChange={set("bank_account_number")}
              placeholder="12-3456-7890123-00"
              className="mt-1.5 rounded-sm border-zinc-400 font-mono"
              data-testid="billing-bank-number"
            />
          </div>
          <div>
            <Label className="label-tag" htmlFor="bs-email">Billing contact email</Label>
            <Input
              id="bs-email"
              type="email"
              value={form.billing_contact_email}
              onChange={set("billing_contact_email")}
              placeholder="accounts@yourbusiness.co.nz"
              className="mt-1.5 rounded-sm border-zinc-400"
              data-testid="billing-email"
            />
          </div>
          <div>
            <Label className="label-tag" htmlFor="bs-phone">Billing contact phone</Label>
            <Input
              id="bs-phone"
              value={form.billing_contact_phone}
              onChange={set("billing_contact_phone")}
              placeholder="021 234 567"
              className="mt-1.5 rounded-sm border-zinc-400 font-mono"
              data-testid="billing-phone"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="label-tag" htmlFor="bs-addr">Postal / billing address</Label>
            <Textarea
              id="bs-addr"
              value={form.billing_address}
              onChange={set("billing_address")}
              rows={2}
              placeholder="42 Workshop Rd, Henderson, Auckland 0610"
              className="mt-1.5 rounded-sm border-zinc-400 text-sm"
              data-testid="billing-address"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="label-tag" htmlFor="bs-terms-text">Payment terms &amp; conditions</Label>
            <Textarea
              id="bs-terms-text"
              value={form.payment_terms_text}
              onChange={set("payment_terms_text")}
              rows={3}
              placeholder={"e.g. Payment is due within 20 days of invoice date. Overdue accounts incur interest at 2% per month. All goods remain the property of the company until paid in full."}
              className="mt-1.5 rounded-sm border-zinc-400 text-sm"
              data-testid="billing-payment-terms-text"
            />
            <p className="text-[10px] text-zinc-400 mt-1">Printed on every customer invoice PDF under &quot;Payment terms &amp; conditions&quot;. Pre-fills each new invoice — you can tweak it per invoice.</p>
          </div>
          <div className="md:col-span-2">
            <Label className="label-tag" htmlFor="bs-footer">Invoice footer note (optional)</Label>
            <Textarea
              id="bs-footer"
              value={form.invoice_footer_note}
              onChange={set("invoice_footer_note")}
              rows={2}
              placeholder="Thanks for the work, mate — kia ora! Any queries call Dave on 021 234 567."
              className="mt-1.5 rounded-sm border-zinc-400 text-sm"
              data-testid="billing-footer-note"
            />
            <p className="text-[10px] text-zinc-400 mt-1">Italicised line at the bottom of every customer invoice PDF.</p>
          </div>
        </div>
      )}
    </div>
  );
}


// Overtime rules — time-and-a-half etc, auto-applied on the Payroll screen.
function OvertimeSettings({ canManage }) {
  const [form, setForm] = useState({
    overtime_enabled: false,
    overtime_multiplier: "1.5",
    overtime_weekly_threshold: "",
    overtime_weekends: false,
  });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/org/me")
      .then(({ data }) => setForm({
        overtime_enabled: !!data?.overtime_enabled,
        overtime_multiplier: String(data?.overtime_multiplier || 1.5),
        overtime_weekly_threshold: data?.overtime_weekly_threshold ? String(data.overtime_weekly_threshold) : "",
        overtime_weekends: !!data?.overtime_weekends,
      }))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/org/me", {
        overtime_enabled: form.overtime_enabled,
        overtime_multiplier: parseFloat(form.overtime_multiplier) || 1.5,
        overtime_weekly_threshold: parseFloat(form.overtime_weekly_threshold) || 0,
        overtime_weekends: form.overtime_weekends,
      });
      toast.success("Overtime rules saved — payroll will apply them automatically");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>;

  return (
    <div data-testid="overtime-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-tag flex items-center gap-1.5"><Clock3 className="w-3 h-3" /> Overtime rules</div>
          <h2 className="font-semibold tracking-tight text-lg mt-2">
            Time and a half, done for you
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            When enabled, the Payroll screen automatically pays hours over your weekly threshold — and/or weekend hours — at the multiplier below. No spreadsheets.
          </p>
        </div>
        {canManage && (
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="overtime-save-btn">
            <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save overtime rules"}
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-zinc-200 dark:border-white/10 rounded-lg p-3.5 flex items-center justify-between gap-3">
          <div>
            <div className="text-[12px] font-bold">Pay overtime</div>
            <div className="text-[11px] text-zinc-500">Master switch</div>
          </div>
          <Switch checked={form.overtime_enabled} disabled={!canManage}
                  onCheckedChange={(v) => setForm({ ...form, overtime_enabled: v })}
                  data-testid="overtime-enabled-switch" />
        </div>
        <div className="border border-zinc-200 dark:border-white/10 rounded-lg p-3.5">
          <Label className="text-[12px] font-bold">Overtime rate</Label>
          <Select value={form.overtime_multiplier} onValueChange={(v) => setForm({ ...form, overtime_multiplier: v })} disabled={!canManage}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="overtime-multiplier-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1.25">Time and a quarter (×1.25)</SelectItem>
              <SelectItem value="1.5">Time and a half (×1.5)</SelectItem>
              <SelectItem value="2">Double time (×2)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="border border-zinc-200 dark:border-white/10 rounded-lg p-3.5">
          <Label className="text-[12px] font-bold">After weekly hours</Label>
          <Input type="number" min="0" step="0.5" value={form.overtime_weekly_threshold} disabled={!canManage}
                 onChange={(e) => setForm({ ...form, overtime_weekly_threshold: e.target.value })}
                 placeholder="e.g. 40 (blank = off)"
                 className="mt-2 rounded-sm border-zinc-400" data-testid="overtime-threshold-input" />
          <div className="text-[10.5px] text-zinc-500 mt-1">Hours past this in a pay week get the overtime rate.</div>
        </div>
        <div className="border border-zinc-200 dark:border-white/10 rounded-lg p-3.5 flex items-center justify-between gap-3">
          <div>
            <div className="text-[12px] font-bold">Weekends at OT rate</div>
            <div className="text-[11px] text-zinc-500">All Sat &amp; Sun hours</div>
          </div>
          <Switch checked={form.overtime_weekends} disabled={!canManage}
                  onCheckedChange={(v) => setForm({ ...form, overtime_weekends: v })}
                  data-testid="overtime-weekends-switch" />
        </div>
      </div>
    </div>
  );
}

// Company notification switches — silence the automatic dispatch emails and the
// "scheduled to…" chat posts when a schedule is published. Defaults on.
function NotificationSettings({ canManage }) {
  const [form, setForm] = useState({ email: true, chat: true });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/org/me")
      .then(({ data }) => setForm({
        email: data?.schedule_email_notifications !== false,
        chat: data?.schedule_chat_notifications !== false,
      }))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/org/me", {
        schedule_email_notifications: form.email,
        schedule_chat_notifications: form.chat,
      });
      toast.success("Notification settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>;

  return (
    <div data-testid="notification-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-tag flex items-center gap-1.5"><Bell className="w-3 h-3" /> Notifications</div>
          <h2 className="font-semibold tracking-tight text-lg mt-2">
            Team notifications
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Control the automatic messages your crew gets when the schedule changes. Turning these off doesn't change the schedule itself — workers still see their jobs in the app and still get the push alert on their phone.
          </p>
        </div>
        {canManage && (
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="notifications-save-btn">
            <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save notifications"}
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border border-zinc-200 dark:border-white/10 rounded-lg p-3.5 flex items-center justify-between gap-3">
          <div>
            <div className="text-[12px] font-bold">Schedule emails to workers</div>
            <div className="text-[11px] text-zinc-500">Emails sent when a worker's dispatch changes</div>
          </div>
          <Switch checked={form.email} disabled={!canManage}
                  onCheckedChange={(v) => setForm({ ...form, email: v })}
                  data-testid="notif-email-switch" />
        </div>
        <div className="border border-zinc-200 dark:border-white/10 rounded-lg p-3.5 flex items-center justify-between gap-3">
          <div>
            <div className="text-[12px] font-bold">Schedule posts in company chat</div>
            <div className="text-[11px] text-zinc-500">The &quot;📅 scheduled to…&quot; messages in General chat</div>
          </div>
          <Switch checked={form.chat} disabled={!canManage}
                  onCheckedChange={(v) => setForm({ ...form, chat: v })}
                  data-testid="notif-chat-switch" />
        </div>
      </div>
    </div>
  );
}

// Fixed business overheads (yard rent, phone plans, insurance…). Admin only.
const FREQ_LABEL = { weekly: "per week", monthly: "per month", annual: "per year" };
const WEEKLY_FACTOR = { weekly: 1, monthly: 12 / 52, annual: 1 / 52 };

function OverheadsSettings() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/org/me")
      .then(({ data }) => setRows((data?.overheads || []).map((o) => ({ ...o }))))
      .finally(() => setLoaded(true));
  }, []);

  const addRow = () => setRows((r) => [...r, { id: crypto.randomUUID(), name: "", amount: "", frequency: "weekly" }]);
  const update = (id, k, v) => setRows((r) => r.map((o) => (o.id === id ? { ...o, [k]: v } : o)));
  const removeRow = (id) => setRows((r) => r.filter((o) => o.id !== id));

  const weeklyTotal = rows.reduce((s, o) => s + (parseFloat(o.amount) || 0) * (WEEKLY_FACTOR[o.frequency] || 1), 0);

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = rows
        .filter((o) => (o.name || "").trim() && parseFloat(o.amount) > 0)
        .map((o) => ({ id: o.id, name: o.name.trim(), amount: parseFloat(o.amount), frequency: o.frequency || "weekly" }));
      await api.patch("/org/me", { overheads: cleaned });
      setRows(cleaned);
      toast.success("Overheads saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>;

  return (
    <div data-testid="overheads-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-tag flex items-center gap-1.5"><Banknote className="w-3 h-3" /> Business overheads · Admin only</div>
          <h2 className="font-semibold tracking-tight text-lg mt-2">What it costs to open the gate</h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Yard rent, phone plans, insurance, software — anything you pay regardless of work done. These feed the admin-only Business Pulse on your Overview.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Weekly total</div>
          <div className="font-display font-black text-2xl tabular-nums" data-testid="overheads-weekly-total">
            ${weeklyTotal.toLocaleString("en-NZ", { maximumFractionDigits: 0 })}<span className="text-xs font-mono text-zinc-400">/wk</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="border-2 border-dashed border-zinc-300 dark:border-white/15 rounded-lg px-4 py-3 text-sm text-zinc-500">
            No overheads yet — add yard rental, phone plans, insurance…
          </div>
        )}
        {rows.map((o) => (
          <div key={o.id} className="grid grid-cols-[1fr_120px_130px_36px] gap-2 items-center" data-testid={`overhead-row-${o.id}`}>
            <Input value={o.name} onChange={(e) => update(o.id, "name", e.target.value)} placeholder="e.g. Yard rental" className="rounded-sm border-zinc-400" data-testid={`overhead-name-${o.id}`} />
            <Input type="number" min="0" step="0.01" value={o.amount} onChange={(e) => update(o.id, "amount", e.target.value)} placeholder="$" className="rounded-sm border-zinc-400" data-testid={`overhead-amount-${o.id}`} />
            <Select value={o.frequency} onValueChange={(v) => update(o.id, "frequency", v)}>
              <SelectTrigger className="rounded-sm border-zinc-400"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FREQ_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="icon" variant="ghost" className="h-9 w-9 text-zinc-400 hover:text-red-600" onClick={() => removeRow(o.id)} data-testid={`overhead-remove-${o.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" onClick={addRow} className="rounded-sm uppercase tracking-wider font-bold border-zinc-400" data-testid="overhead-add-btn">
          + Add overhead
        </Button>
        <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="overheads-save-btn">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save overheads"}
        </Button>
      </div>
    </div>
  );
}
